import { motion } from "framer-motion";
import { Check, X, Zap, TrendingUp } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const initialIncludes = [
  "Configuración del asistente IA personalizado para AEROBIC",
  "Conexión con WhatsApp Business API (número único)",
  "Desarrollo del panel de administración y conversaciones",
  "Integración con CRM ABC EVO",
  "Configuración de flujos de derivación a asesoras",
  "Capacitación del equipo y documentación",
  "30 días de soporte post-lanzamiento",
];

const fixedIncludes = [
  { text: "Soporte técnico", ok: true },
  { text: "Resolución de incidencias", ok: true },
  { text: "Actualizaciones del sistema", ok: true },
  { text: "Atención (días hábiles)", ok: true },
  { text: "Monitoreo periódico", ok: true },
  { text: "Ajustes puntuales según necesidades", ok: true },
  { text: "No incluye gastos externos (Meta API, OpenAI, hosting)", ok: false },
];

const variableIncludes = [
  { text: "Soporte técnico", ok: true },
  { text: "Atención 24/7", ok: true },
  { text: "Actualizaciones periódicas", ok: true },
  { text: "Resolución de incidencias", ok: true },
  { text: "Ajustes y mejoras continuas", ok: true },
  { text: "Gastos de Meta (plantillas, consumo, etc.) incluidos", ok: true },
  { text: "Costos de tokens OpenAI API incluidos", ok: true },
  { text: "Google Cloud hosting incluido", ok: true },
  { text: "Monitoreo constante", ok: true },
  { text: "Escalabilidad integrada", ok: true },
  { text: "Modelo de rendimiento compartido", ok: true },
];

export default function Pricing() {
  return (
    <section
      id="precios"
      data-testid={LANDING.pricingSection}
      className="bg-[#F9FAFB] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-[#0EA5E9] text-sm font-semibold tracking-widest uppercase mb-3">
            Propuesta económica
          </span>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-700 text-gray-900 tracking-tight">
            Inversión y modalidades de servicio
          </h2>
          <p className="mt-3 text-gray-500 text-base max-w-xl mx-auto">
            Una inversión inicial para desarrollar e implementar el sistema y
            luego la modalidad de mantenimiento que mejor se adapte al modelo de AEROBIC.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-full">
            <span className="w-2 h-2 bg-[#38BDF8] rounded-full flex-shrink-0"></span>
            Propuesta diseñada para una operación de hasta 10 locales
          </div>
        </motion.div>

        {/* Phase 1: Initial investment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="mb-8 bg-orange-100 border border-orange-200 rounded-2xl p-8 shadow-sm"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2.5 py-1 rounded-full">
                  Fase 1 · Pago único
                </span>
              </div>
              <h3 className="font-['Outfit'] text-2xl font-700 text-gray-900 mb-1">
                Desarrollo e Implementación
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                Incluye todo lo necesario para tener el sistema funcionando desde el primer día.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {initialIncludes.map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <Check size={14} className="text-[#38BDF8] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Inversión única</p>
              <p className="font-['Outfit'] text-5xl font-800 text-gray-900">
                USD 3.270
              </p>
              <p className="text-gray-400 text-xs mt-1">+ IVA si aplica</p>
              <div className="mt-3 bg-orange-100 border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full text-center">
                Hasta 10 locales incluidos
              </div>
              <a
                href="#contacto"
                className="mt-4 inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
              >
                Consultar
              </a>
            </div>
          </div>
        </motion.div>

        {/* Phase 2 label */}
        <p className="text-center text-gray-400 text-xs uppercase tracking-widest mb-6">
          Fase 2 · Modalidad de mantenimiento mensual
        </p>

        {/* Monthly options — Variable primero */}
        <div className="grid md:grid-cols-2 gap-5 mb-8">

          {/* Plan Variable — PRIMERO, tinte celeste suave */}
          <motion.div
            data-testid={LANDING.pricingVariableCard}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="bg-emerald-100 border border-emerald-200 rounded-2xl p-7 shadow-sm relative card-hover overflow-hidden"
          >
            {/* Línea de color superior */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#38BDF8] rounded-t-2xl" />
            <div className="flex items-center gap-2 mb-1 mt-1">
              <TrendingUp size={16} className="text-[#38BDF8]" />
              <h3 className="font-['Outfit'] text-lg font-700 text-gray-900">
                Porcentaje / Comisión
              </h3>
            </div>
            <p className="text-gray-500 text-sm mb-5">
              Sin cuota mensual fija. Modelo de rendimiento compartido. Incluye todos los gastos externos de infraestructura.
            </p>
            <div className="mb-1">
              <span className="font-['Outfit'] text-5xl font-800 text-gray-900">$0</span>
              <span className="text-gray-400 text-sm">/por rendimiento</span>
            </div>
            <p className="text-gray-500 text-xs mb-6">
              Se define un porcentaje sobre las nuevas inscripciones atribuidas al sistema
            </p>
            <ul className="space-y-2.5">
              {variableIncludes.map((item) => (
                <li key={item.text} className="flex items-start gap-2">
                  <Check size={14} className="text-[#38BDF8] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{item.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 bg-amber-50 border border-amber-100 rounded-xl p-3">
              <p className="text-amber-700 text-xs leading-relaxed">
                <span className="font-semibold">Nota:</span> Para definir esta modalidad se requiere un análisis previo del flujo estimado de ventas y operaciones del grupo, con el fin de establecer un porcentaje justo y sostenible para ambas partes.
              </p>
            </div>
            <a
              href="#contacto"
              className="mt-5 block text-center bg-[#38BDF8] hover:bg-[#0EA5E9] text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              Consultar esta modalidad
            </a>
          </motion.div>

          {/* Plan Fijo — SEGUNDO, tinte gris suave */}
          <motion.div
            data-testid={LANDING.pricingFixedCard}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="bg-sky-100 border border-sky-200 rounded-2xl p-7 shadow-sm relative card-hover overflow-hidden"
          >
            {/* Línea de color superior */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-slate-400 rounded-t-2xl" />
            <div className="flex items-center gap-2 mb-1 mt-1">
              <Zap size={16} className="text-slate-400" />
              <h3 className="font-['Outfit'] text-lg font-700 text-gray-900">
                Mantenimiento Fijo
              </h3>
            </div>
            <p className="text-gray-500 text-sm mb-5">
              Costo mensual predecible. Los gastos de servicios externos (Meta, OpenAI, hosting) corren por cuenta del cliente.
            </p>
            <div className="mb-5">
              <span className="font-['Outfit'] text-5xl font-800 text-gray-900">USD 630</span>
              <span className="text-gray-400 text-sm">/por mes</span>
            </div>
            <ul className="space-y-2.5">
              {fixedIncludes.map((item) => (
                <li key={item.text} className="flex items-start gap-2">
                  {item.ok ? (
                    <Check size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <X size={14} className="text-red-300 mt-0.5 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${item.ok ? "text-gray-700" : "text-gray-400"}`}>
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
            <a
              href="#contacto"
              className="mt-7 block text-center border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              Elegir esta modalidad
            </a>
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.3 }}
          className="text-center text-gray-400 text-xs"
        >
          Los valores son orientativos y pueden ajustarse según el alcance final acordado.
          <br />
          Todos los valores en USD. Se puede combinar con pesos uruguayos a tipo de cambio acordado.
        </motion.p>
      </div>
    </section>
  );
}
